import { useState } from "react";
import { Download, Loader2, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCallRecordingDownload, useDeleteCallRecording } from "@/hooks/use-calls";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import "./i18n.js";

export function CallRecordingActions({
  callId,
  canDelete,
  showDownload = true,
  compact = true,
  onDeleted,
}: {
  callId: string;
  canDelete: boolean;
  showDownload?: boolean;
  compact?: boolean;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const download = useCallRecordingDownload(callId, {
    onSuccess: ({ downloadUrl, url }) => {
      const link = document.createElement("a");
      link.href = downloadUrl ?? url;
      link.download = `ligacao-${callId}.wav`;
      link.rel = "noopener";
      link.click();
    },
    onError: () => toast.error(t("callRecording.downloadError")),
  });

  // A invalidação (["calls"], ["call-recording", callId], ["billing"],
  // ["subscription"]) mora no hook — é a mesma para todo chamador. Aqui fica só
  // o que é desta tela, e na mesma ordem de antes: fechar, avisar, depois o cache.
  const remove = useDeleteCallRecording(callId, {
    onSuccess: () => {
      setConfirming(false);
      onDeleted?.();
      toast.success(t("callRecording.deleted"));
    },
    onError: () => toast.error(t("callRecording.deleteError")),
  });

  return (
    <div
      className="flex items-center justify-end gap-1"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {showDownload ? (
        <Button
          type="button"
          variant="ghost"
          size={compact ? "icon-sm" : "sm"}
          aria-label={t("callRecording.download")}
          title={compact ? t("callRecording.download") : undefined}
          disabled={download.isPending || remove.isPending}
          onClick={() => download.mutate()}
        >
          {download.isPending ? <Loader2 className="animate-spin" /> : <Download />}
          {!compact ? t("callRecording.download") : null}
        </Button>
      ) : null}

      {canDelete ? (
        <>
          <Button
            type="button"
            variant="ghost"
            size={compact ? "icon-sm" : "sm"}
            className="text-destructive hover:text-destructive"
            aria-label={t("callRecording.delete")}
            title={compact ? t("callRecording.delete") : undefined}
            disabled={download.isPending || remove.isPending}
            onClick={() => setConfirming(true)}
          >
            <Trash2 />
            {!compact ? t("callRecording.delete") : null}
          </Button>

          <AlertDialog open={confirming} onOpenChange={setConfirming}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("callRecording.confirm.title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("callRecording.confirm.description")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={remove.isPending}>
                  {t("callRecording.confirm.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  disabled={remove.isPending}
                  onClick={(event) => {
                    event.preventDefault();
                    remove.mutate();
                  }}
                >
                  {remove.isPending ? <Loader2 className="animate-spin" /> : null}
                  {t("callRecording.confirm.action")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
    </div>
  );
}
